import api, { rawApi } from './axiosClient'
import { applyPinEnabledFromUserProfile } from '../../util/pinBrowserFlags'
import {
    AddMobileNumberRequest,
    AddMobileNumberResponse,
    ResendVerificationCodeResponse,
    SendEmailOtpResponse,
    SendPinVerificationOtpRequest,
    SendPinVerificationOtpResponse,
    User,
    VerifyMobileNumberRequest
} from '../../proto/generated/identity'
import store from '../../store'
import { updateUserPreferencesInState, UserPreferences } from '../../reducer/user.reducer'
import { setUserDetails} from '../../reducer/user.reducer'
import { SubscriptionFeatureResponse, SubscriptionPlansResponse, TrialEligibilityResponse, UserSubscriptionsResponse } from '../../proto/generated/subscription'
import { PremiumFeatureResponse } from '../../proto/generated/subscription'

export const getUserProfile = async (): Promise<User> => {
    return new Promise((resolve, reject) => {
        const url = `/user/me`
        api.get(url, {
            headers: {
                'Accept': 'application/json'
            },
            responseType: 'json'
        }).then(async (response) => {
            applyPinEnabledFromUserProfile(response.data)
            resolve(response.data)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const getUserProfileWithRawRequest = async (): Promise<any> => {
    return new Promise((resolve, reject) => {
        const url = `/user/me`
        rawApi.get(url, {
            headers: {
                'Accept': 'application/json'
            },
            responseType: 'json'
        }).then(async (response) => {
            applyPinEnabledFromUserProfile(response.data)
            resolve(response.data)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const addMobileNumber = async (args: { body: AddMobileNumberRequest }): Promise<AddMobileNumberResponse> => {
    return new Promise((resolve, reject) => {
        const url = `/user/add-mobile-number`
        api.post(url, JSON.stringify(args.body), {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = AddMobileNumberResponse.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            console.log('caught error', error)
            reject(error)
        })
    })
}

export const verifyMobileNumber = async (args: { body: VerifyMobileNumberRequest }): Promise<User> => {
    return new Promise((resolve, reject) => {
        const url = `/user/verify-mobile-number`
        api.post(url, JSON.stringify(args.body), {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = User.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const sendPinVerificationOtp = async (args: {
    body: SendPinVerificationOtpRequest
}): Promise<SendPinVerificationOtpResponse> => {
    return new Promise((resolve, reject) => {
        const url = `/user/send-pin-verification-otp`
        const body = SendPinVerificationOtpRequest.fromPartial(args.body)
        api
            .post(url, JSON.stringify(SendPinVerificationOtpRequest.toJSON(body)), {
                headers: {
                    Accept: 'application/x-protobuf',
                    'Content-Type': 'application/json',
                },
                responseType: 'arraybuffer',
            })
            .then(async (response) => {
                const decodedData = SendPinVerificationOtpResponse.decode(new Uint8Array(response.data))
                resolve(decodedData)
            })
            .catch((error) => {
                reject(error)
            })
    })
}

export const resendVerificationCode = async (): Promise<ResendVerificationCodeResponse> => {
    return new Promise((resolve, reject) => {
        const url = `/user/resend-verification-code`
        api.post(url, {}, {
            headers: {
                'Accept': 'application/x-protobuf'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = ResendVerificationCodeResponse.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const requestChangeEmailOtp = async (newEmail: string): Promise<string> => {
    const res = await api.post(
        '/user/change-email',
        JSON.stringify({ newEmail }),
        {
            headers: {
                Accept: 'application/x-protobuf',
                'Content-Type': 'application/json',
                // Caller shows validation messages (e.g. email already taken).
                'X-Suppress-Error': 'true',
            },
            responseType: 'arraybuffer',
        }
    )
    const decoded = SendEmailOtpResponse.decode(new Uint8Array(res.data))
    if (!decoded.requestId) {
        throw new Error('Failed to start email verification')
    }
    return decoded.requestId
}

export const verifyEmailChange = async (args: {
    newEmail: string
    requestId: string
    verificationOtp: string
}): Promise<User> => {
    const res = await api.post(
        '/user/verify-email-change',
        JSON.stringify({
            newEmail: args.newEmail,
            requestId: args.requestId,
            verificationOtp: args.verificationOtp,
        }),
        {
            headers: {
                Accept: 'application/x-protobuf',
                'Content-Type': 'application/json',
                'X-Suppress-Error': 'true',
            },
            responseType: 'arraybuffer',
        }
    )
    return User.decode(new Uint8Array(res.data))
}

export const updateUserAttributes = async (args: { body: any }): Promise<User> => {
    return new Promise((resolve, reject) => {
        const url = `/user/update-user-attributes`
        api.post(url, JSON.stringify(args.body), {
            headers: {
                'Accept': 'application/x-protobuf',
                'Content-Type': 'application/json'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = User.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            console.log('caught error', error)
            reject(error)
        })
    })
}

export const updateUserPreferences = async (args: { body: Partial<UserPreferences> }): Promise<User> => {
    return new Promise((resolve, reject) => {
        const url = `/user/update-user-preferences`
        api.post(url, JSON.stringify(args.body), {
            headers: {
                'Accept': 'application/x-protobuf',
                'Content-Type': 'application/json'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = User.decode(new Uint8Array(response.data))
            const preferences = JSON.parse(decodedData.preferences || '{}')
            const currentPreferences = store.getState().userStore.userDetails.preferences
            store.dispatch(updateUserPreferencesInState({
                ...currentPreferences,
                ...preferences,
                // Quick widgets are updated optimistically in Redux; stale API responses must not reopen them.
                quickWidgets: currentPreferences.quickWidgets ?? preferences.quickWidgets,
            }))
            resolve(decodedData)
        }).catch((error) => {
            console.log('caught error', error)
            reject(error)
        })
    })
}



export const getPaymentPlan = async (): Promise<SubscriptionPlansResponse> => {
    return new Promise((resolve, reject) => {
        const url = `/payment/plans`
        api.get(url, {
            headers: {
                'Accept': 'application/x-protobuf',
                'Content-Type': 'application/json'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = SubscriptionPlansResponse.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const getUserSubscriptionPlanFeatures = async (): Promise<SubscriptionFeatureResponse> => { // features
    return new Promise((resolve, reject) => {
        const url = `/subscription-plan/features`
        api.get(url, {
            headers: {
                'Accept': 'application/x-protobuf',
                'Content-Type': 'application/json'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = SubscriptionFeatureResponse.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })
}

export const getUserSubscriptionCurrentStatus = async (): Promise<UserSubscriptionsResponse> => { // Active status
    return new Promise((resolve, reject) => {
        const url = `/subscription-plan/user-subscription`
        api.get(url, {
            headers: {
                'Accept': 'application/x-protobuf',
                'Content-Type': 'application/json'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = UserSubscriptionsResponse.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })

}

export const getUserSubscriptionPlanAllFeatures = async (): Promise<PremiumFeatureResponse> => { //all-features
    return new Promise((resolve, reject) => {
        const url = `/subscription-plan/all-features`
        api.get(url, {
            headers: {
                'Accept': 'application/x-protobuf',
                'Content-Type': 'application/json'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = PremiumFeatureResponse.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })

}

export const getUserSubscriptionPlan = async (): Promise<SubscriptionPlansResponse> => { //plans
    return new Promise((resolve, reject) => {
        const url = `/subscription-plan/plans`
        api.get(url, {
            headers: {
                'Accept': 'application/x-protobuf',
                'Content-Type': 'application/json'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = SubscriptionPlansResponse.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })

}

export const getUserSubscriptionCheckEligibilityForFreeTrail = async (planId: string): Promise<TrialEligibilityResponse> => {
    return new Promise((resolve, reject) => {
        const url = `/subscription-plan/trial-eligibility/${planId}` // check the plan eligibility for free plan
        api.get(url, {
            headers: {
                'Accept': 'application/x-protobuf',
                'Content-Type': 'application/json'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = TrialEligibilityResponse.decode(new Uint8Array(response.data))
            resolve(decodedData)
        }).catch((error) => {
            reject(error)
        })
    })

}


export const getUserSubscriptionTrailPlanUpdate = async (args: { planId: string }): Promise<User> => {
    return new Promise((resolve, reject) => {
        const { planId } = args;
        const url = `/subscription-plan/trial/${planId}` // if userid eligibly true for free trail
        api.post(url, {
            headers: {
                'Accept': 'application/x-protobuf',
                'Content-Type': 'application/json'
            },
            responseType: 'arraybuffer'
        }).then(async (response) => {
            const decodedData = User.decode(new Uint8Array(response.data))
           
        resolve(decodedData)
        }).catch((error) => {
            console.log('caught error', error)
            reject(error)
        })
    })

}




